'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { Voicemail, Play, Pause, Check, RefreshCw, Settings, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

interface VoicemailEntry {
  id: string;
  callId: string | null;
  callerNumber: string;
  patientName: string | null;
  recordingUrl: string | null;
  recordingSid: string | null;
  durationSeconds: number;
  transcription: string | null;
  listenedBy: string[];
  createdAt: string;
}

interface VoicemailConfig {
  isEnabled: boolean;
  triggerAfterSeconds: number;
  greetingMessage: string;
  notifyOperatorIds: string[];
}

/**
 * Extract the recording SID from a Twilio recording URL.
 * Twilio URLs look like: https://api.twilio.com/2010-04-01/Accounts/ACxxx/Recordings/REyyy
 * or https://api.twilio.com/2010-04-01/Accounts/ACxxx/Recordings/REyyy.mp3
 */
function extractRecordingSid(url: string): string | null {
  const match = url.match(/Recordings\/(RE[a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

/**
 * Returns the proxied audio URL for a recording.
 * If the URL is a Twilio URL, route through our server-side proxy.
 * Otherwise return the URL as-is.
 */
function getProxiedAudioUrl(recordingUrl: string): string {
  if (recordingUrl.includes('api.twilio.com') || recordingUrl.includes('twilio.com')) {
    const sid = extractRecordingSid(recordingUrl);
    if (sid) {
      return `/api/twilio/recording/${sid}`;
    }
  }
  return recordingUrl;
}

export default function VoicemailPage() {
  const { session } = useAuth();
  const [entries, setEntries] = useState<VoicemailEntry[]>([]);
  const [config, setConfig] = useState<VoicemailConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [configForm, setConfigForm] = useState<VoicemailConfig>({ isEnabled: true, triggerAfterSeconds: 30, greetingMessage: '', notifyOperatorIds: [] });
  const [saving, setSaving] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const operatorId = typeof window !== 'undefined' ? (localStorage.getItem('lcm_current_operator_id') ?? 'op-1') : 'op-1';

  const load = useCallback(async () => {
    if (!session) return;
    try {
      const [entriesRes, configRes] = await Promise.all([
        fetch('/api/calls/voicemail?type=entries'),
        fetch('/api/calls/voicemail'),
      ]);
      const entriesJson = await entriesRes.json();
      const configJson = await configRes.json();
      setEntries(entriesJson.data ?? []);
      setConfig(configJson.data);
      if (configJson.data) setConfigForm(configJson.data);
    } catch { /* keep */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkListened = async (entryId: string) => {
    try {
      await fetch('/api/calls/voicemail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_listened', entryId, operatorId }),
      });
      load();
    } catch { /* ignore */ }
  };

  const handlePlay = (entry: VoicemailEntry) => {
    if (!entry.recordingUrl) {
      toast.error('Nessuna registrazione disponibile per questo messaggio');
      return;
    }
    if (playingId === entry.id) {
      // Stop current playback
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Use proxied URL to avoid CORS/auth issues with Twilio
    const proxiedUrl = getProxiedAudioUrl(entry.recordingUrl);
    const audio = new Audio(proxiedUrl);
    audioRef.current = audio;
    audio.play().catch(() => {
      toast.error('Impossibile riprodurre la registrazione.');
    });
    audio.onended = () => {
      setPlayingId(null);
      handleMarkListened(entry.id);
    };
    audio.onerror = () => {
      setPlayingId(null);
      toast.error('Errore nella riproduzione audio');
    };
    setPlayingId(entry.id);
  };

  const handleSaveConfig = async () => {
    if (configForm.triggerAfterSeconds < 5) {
      toast.error('Il tempo di attivazione deve essere almeno 5 secondi');
      return;
    }
    if (configForm.triggerAfterSeconds > 300) {
      toast.error('Il tempo di attivazione non può superare 300 secondi');
      return;
    }
    if (!configForm.greetingMessage.trim()) {
      toast.error('Il messaggio di benvenuto è obbligatorio');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/calls/voicemail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_config', ...configForm }),
      });
      if (!res.ok) throw new Error();
      toast.success('Configurazione salvata');
      setShowConfig(false);
      load();
    } catch { toast.error('Errore nel salvare'); } finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Voicemail Automatica</h1>
              <p className="text-sm text-muted-foreground mt-1">Messaggi vocali quando nessun operatore è disponibile</p>
            </div>
            <div className="flex gap-2">
              <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm hover:bg-muted transition-colors">
                <RefreshCw size={14} />
              </button>
              <button onClick={() => setShowConfig(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm hover:bg-muted transition-colors">
                <Settings size={14} /> Configura
              </button>
            </div>
          </div>

          {config && (
            <div className={`border rounded-xl p-4 mb-6 flex items-center gap-3 ${config.isEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`w-2.5 h-2.5 rounded-full ${config.isEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Voicemail {config.isEnabled ? 'attiva' : 'disattiva'} — si attiva dopo {config.triggerAfterSeconds}s senza risposta
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-lg">"{config.greetingMessage}"</p>
              </div>
            </div>
          )}

          {showConfig && (
            <div className="bg-white border border-border rounded-xl p-5 mb-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Configurazione Voicemail</h3>
                <button onClick={() => setShowConfig(false)}><X size={16} className="text-muted-foreground" /></button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="vmEnabled" checked={configForm.isEnabled} onChange={e => setConfigForm(f => ({ ...f, isEnabled: e.target.checked }))}
                    className="w-4 h-4 rounded border-border" />
                  <label htmlFor="vmEnabled" className="text-sm font-medium text-foreground">Abilita voicemail automatica</label>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Attiva dopo (secondi)</label>
                  <input type="number" value={configForm.triggerAfterSeconds} onChange={e => setConfigForm(f => ({ ...f, triggerAfterSeconds: +e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Messaggio di benvenuto</label>
                  <textarea value={configForm.greetingMessage} onChange={e => setConfigForm(f => ({ ...f, greetingMessage: e.target.value }))} rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none" />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={handleSaveConfig} disabled={saving}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors">
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-16 text-center"><div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" /></div>
          ) : entries.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Voicemail size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nessun messaggio vocale</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map(entry => {
                const listened = entry.listenedBy.includes(operatorId);
                const isPlaying = playingId === entry.id;
                const proxiedUrl = entry.recordingUrl ? getProxiedAudioUrl(entry.recordingUrl) : null;
                return (
                  <div key={entry.id} className={`bg-white border rounded-xl p-4 shadow-sm ${!listened ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {!listened && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                          <span className="text-sm font-semibold text-foreground">{entry.patientName ?? entry.callerNumber}</span>
                          {entry.patientName && <span className="text-xs text-muted-foreground">{entry.callerNumber}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          {new Date(entry.createdAt).toLocaleString('it-IT')} · {entry.durationSeconds}s
                        </p>

                        {/* Audio player — uses proxied URL to bypass Twilio CORS/auth */}
                        {proxiedUrl && (
                          <div className="mb-3">
                            <audio
                              controls
                              src={proxiedUrl}
                              className="w-full h-8"
                              onPlay={() => setPlayingId(entry.id)}
                              onPause={() => setPlayingId(null)}
                              onEnded={() => { setPlayingId(null); handleMarkListened(entry.id); }}
                              style={{ height: '32px' }}
                            />
                          </div>
                        )}

                        {/* Transcription */}
                        {entry.transcription ? (
                          <div className="bg-muted/50 rounded-lg p-3 mb-2">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Trascrizione</p>
                            <p className="text-sm text-foreground italic">"{entry.transcription}"</p>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic mb-2">Nessuna trascrizione disponibile</p>
                        )}

                        <p className="text-xs text-muted-foreground">{entry.listenedBy.length} operatore/i ha ascoltato</p>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {entry.recordingUrl && (
                          <button
                            onClick={() => handlePlay(entry)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs transition-colors ${isPlaying ? 'bg-primary text-white border-primary' : 'border-border hover:bg-muted'}`}
                          >
                            {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                            {isPlaying ? 'Pausa' : 'Ascolta'}
                          </button>
                        )}
                        {!listened && (
                          <button onClick={() => handleMarkListened(entry.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg text-xs hover:bg-green-100 transition-colors">
                            <Check size={12} /> Ascoltato
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
